import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Bell, CheckCheck, Wrench, Calendar, MessageSquare, Star } from 'lucide-react';

const TYPE_ICONS = {
  repair_progress: Wrench,
  appointment: Calendar,
  new_bid: MessageSquare,
  review: Star,
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} d`;
  return new Date(dateStr).toLocaleDateString('es-CR', { day: 'numeric', month: 'short' });
};

export const NotificationsDropdown = ({ API, sessionToken }) => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const headers = { Authorization: `Bearer ${sessionToken}` };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      setNotifications(res.data);
    } catch (e) {
      // non-critical
    }
  }, [API, sessionToken]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markRead = async (notif) => {
    if (notif.read) return;
    setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
    try {
      await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers });
    } catch (e) {
      // non-critical
    }
  };

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers });
    } catch (e) {
      // non-critical
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
          data-testid="notifications-bell"
          aria-label="Notificaciones"
        >
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="notifications-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,380px)] p-0 rounded-xl" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="font-bold text-sm text-slate-900">Notificaciones</p>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllRead}
              className="h-7 text-xs text-purple-700 hover:text-purple-900 hover:bg-purple-50 px-2"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" /> Marcar todas
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="py-10 text-center" data-testid="notifications-empty">
            <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No tienes notificaciones</p>
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-50">
            {notifications.map((n) => {
              const Icon = TYPE_ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id}
                  onClick={() => markRead(n)}
                  className={`w-full text-left px-4 py-3 flex gap-3 transition-colors hover:bg-slate-50 ${!n.read ? 'bg-purple-50/60' : ''}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${!n.read ? 'bg-purple-100' : 'bg-slate-100'}`}>
                    <Icon className={`w-4 h-4 ${!n.read ? 'text-purple-700' : 'text-slate-400'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className={`text-sm truncate ${!n.read ? 'font-bold text-slate-900' : 'font-medium text-slate-600'}`}>{n.title}</p>
                      <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(n.created_at)}</span>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{n.message}</p>
                  </div>
                  {!n.read && <span className="w-2 h-2 bg-purple-600 rounded-full shrink-0 mt-2" />}
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
